// Panel /admin — solo accesible para users con role='admin'.
// Tabs: Usuarios e Invitaciones.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminUser, Invitation } from '@bini-trading/shared-types';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { cn } from '../lib/cn';

type Tab = 'users' | 'invitations';

export default function Admin(): ReactElement {
  const [tab, setTab] = useState<Tab>('users');

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="text-xs text-ink-subtle tracking-[0.18em] uppercase mb-2">
            Administración
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Panel admin</h1>
          <p className="text-ink-muted mt-2">
            Gestión de usuarios e invitaciones. Solo visible para administradores.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-surface-border mb-8">
          <TabButton active={tab === 'users'} onClick={() => setTab('users')}>
            Usuarios
          </TabButton>
          <TabButton active={tab === 'invitations'} onClick={() => setTab('invitations')}>
            Invitaciones
          </TabButton>
        </div>

        {tab === 'users' ? <UsersTab /> : <InvitationsTab />}
      </div>
    </AppLayout>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): ReactElement {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
        active
          ? 'border-brand-500 text-ink'
          : 'border-transparent text-ink-muted hover:text-ink hover:border-surface-border',
      )}
    >
      {children}
    </button>
  );
}

// ─── USERS TAB ──────────────────────────────────────────────────────────────

function UsersTab(): ReactElement {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<AdminUser[]>('/admin/users'),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api<AdminUser>(`/admin/users/${id}`, { method: 'PATCH', body: { active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const toggleRoleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: 'user' | 'admin' }) =>
      api<AdminUser>(`/admin/users/${id}`, { method: 'PATCH', body: { role } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (userId: string) =>
      api<{ temporaryPassword: string; message: string }>(
        `/admin/users/${userId}/reset-password`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      setTempPassword(data.temporaryPassword);
      setResetTarget(null);
    },
  });

  if (isLoading) return <SectionSkeleton label="Cargando usuarios…" />;
  if (error) return <SectionError msg={(error as Error).message} />;
  if (!users) return <SectionEmpty msg="Sin datos." />;

  return (
    <>
      <div className="rounded-xl border border-surface-border bg-surface-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-panel/50 text-ink-subtle uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Usuario</th>
              <th className="text-left px-4 py-3 font-semibold">Email</th>
              <th className="text-left px-4 py-3 font-semibold">Rol</th>
              <th className="text-left px-4 py-3 font-semibold">Estado</th>
              <th className="text-left px-4 py-3 font-semibold">Creado</th>
              <th className="text-right px-4 py-3 font-semibold">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} className="border-t border-surface-border">
                  <td className="px-4 py-3 font-medium">
                    {u.name}
                    {isMe && (
                      <span className="ml-2 text-[10px] text-brand-500 uppercase tracking-wider">
                        vos
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-muted">{u.email}</td>
                  <td className="px-4 py-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="px-4 py-3">
                    <ActiveBadge active={u.active} />
                  </td>
                  <td className="px-4 py-3 text-ink-muted text-xs font-mono">
                    {new Date(u.createdAt).toLocaleDateString('es-CL')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button
                        size="sm"
                        variant={u.active ? 'secondary' : 'primary'}
                        disabled={isMe || toggleActiveMutation.isPending}
                        onClick={() =>
                          toggleActiveMutation.mutate({ id: u.id, active: !u.active })
                        }
                      >
                        {u.active ? 'Desactivar' : 'Activar'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={isMe || toggleRoleMutation.isPending}
                        onClick={() =>
                          toggleRoleMutation.mutate({
                            id: u.id,
                            role: u.role === 'admin' ? 'user' : 'admin',
                          })
                        }
                      >
                        {u.role === 'admin' ? 'Quitar admin' : 'Hacer admin'}
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => setResetTarget(u)}
                      >
                        Reset pwd
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal confirmar reset */}
      <Modal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        title="Resetear password"
        description="Vas a generar una password temporal nueva. La actual queda invalidada al instante."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setResetTarget(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={resetPasswordMutation.isPending}
              onClick={() => resetTarget && resetPasswordMutation.mutate(resetTarget.id)}
            >
              Resetear
            </Button>
          </div>
        }
      >
        <p className="text-sm text-ink-muted">
          {resetTarget && (
            <>
              Confirmás resetear la password de <strong>{resetTarget.name}</strong> ({resetTarget.email})?
              Vas a tener que pasársela por canal seguro (Signal, WhatsApp, en persona).
            </>
          )}
        </p>
      </Modal>

      {/* Modal mostrar password generada */}
      <Modal
        open={!!tempPassword}
        onClose={() => setTempPassword(null)}
        title="Password temporal generada"
        description="Copiala AHORA — solo se muestra una vez."
        size="md"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button
              variant="secondary"
              onClick={() =>
                tempPassword && navigator.clipboard?.writeText(tempPassword).catch(() => {})
              }
            >
              📋 Copiar
            </Button>
            <Button variant="primary" onClick={() => setTempPassword(null)}>
              Listo, la guardé
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <div className="p-4 rounded-lg bg-surface-panel border border-surface-border font-mono text-sm break-all">
            {tempPassword}
          </div>
          <div className="text-xs text-warn-DEFAULT bg-warn-DEFAULT/10 border border-warn-DEFAULT/30 rounded p-3 leading-relaxed">
            ⚠️ Pasala al user por canal seguro. La password vieja del user ya NO sirve;
            solo esta funciona ahora. El user va a poder loguearse y (cuando exista el
            flujo) cambiarla.
          </div>
        </div>
      </Modal>
    </>
  );
}

// ─── INVITATIONS TAB ────────────────────────────────────────────────────────

function InvitationsTab(): ReactElement {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: invitations, isLoading } = useQuery({
    queryKey: ['admin', 'invitations'],
    queryFn: () => api<Invitation[]>('/admin/invitations'),
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) =>
      api<Invitation>('/admin/invitations', { method: 'POST', body: { email } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'invitations'] });
      setEmail('');
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) =>
      api(`/admin/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'invitations'] }),
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return;
    inviteMutation.mutate(email.trim());
  };

  return (
    <>
      {/* Form invitar */}
      <form
        onSubmit={submit}
        className="rounded-xl border border-surface-border bg-surface-elevated p-5 mb-6"
      >
        <div className="text-sm font-semibold mb-3">Invitar a alguien nuevo</div>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@ejemplo.com"
              required
            />
            {error && (
              <div className="mt-2 text-xs text-loss-DEFAULT bg-loss-DEFAULT/10 border border-loss-DEFAULT/30 rounded p-2">
                {error}
              </div>
            )}
          </div>
          <Button
            type="submit"
            variant="primary"
            loading={inviteMutation.isPending}
            disabled={!email.trim()}
          >
            Invitar
          </Button>
        </div>
        <p className="text-xs text-ink-faint mt-3">
          La persona recibirá una invitación implícita (email queda autorizado en signup).
          Por ahora hay que avisarle vos por canal externo: link a https://trading.binimeliz.com/signup
          + decirle que use ese mismo email para registrarse.
        </p>
      </form>

      {/* Tabla */}
      {isLoading ? (
        <SectionSkeleton label="Cargando invitaciones…" />
      ) : !invitations || invitations.length === 0 ? (
        <SectionEmpty msg="Sin invitaciones todavía. Invitá a alguien con el formulario de arriba." />
      ) : (
        <div className="rounded-xl border border-surface-border bg-surface-elevated overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-panel/50 text-ink-subtle uppercase text-[10px] tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Estado</th>
                <th className="text-left px-4 py-3 font-semibold">Invitado por</th>
                <th className="text-left px-4 py-3 font-semibold">Creada</th>
                <th className="text-left px-4 py-3 font-semibold">Aceptada / revocada</th>
                <th className="text-right px-4 py-3 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id} className="border-t border-surface-border">
                  <td className="px-4 py-3 font-medium">{inv.email}</td>
                  <td className="px-4 py-3">
                    <InvitationStatusBadge status={inv.status} />
                  </td>
                  <td className="px-4 py-3 text-ink-muted text-xs">
                    {inv.inviter?.name ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-ink-muted text-xs font-mono">
                    {new Date(inv.createdAt).toLocaleDateString('es-CL')}
                  </td>
                  <td className="px-4 py-3 text-ink-muted text-xs font-mono">
                    {inv.acceptedAt
                      ? `Aceptó ${new Date(inv.acceptedAt).toLocaleDateString('es-CL')}`
                      : inv.revokedAt
                        ? `Revocada ${new Date(inv.revokedAt).toLocaleDateString('es-CL')}`
                        : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {inv.status === 'pending' ? (
                      <Button
                        size="sm"
                        variant="danger"
                        loading={revokeMutation.isPending}
                        onClick={() => revokeMutation.mutate(inv.id)}
                      >
                        Revocar
                      </Button>
                    ) : (
                      <span className="text-xs text-ink-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ─── badges + helpers ───────────────────────────────────────────────────────

function RoleBadge({ role }: { role: string }): ReactElement {
  const cls =
    role === 'admin'
      ? 'border-brand-500/40 bg-brand-500/10 text-brand-500'
      : 'border-surface-border bg-surface-panel text-ink-muted';
  return (
    <span className={cn('text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded font-semibold', cls)}>
      {role}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }): ReactElement {
  const cls = active
    ? 'border-win-DEFAULT/40 bg-win-DEFAULT/10 text-win-DEFAULT'
    : 'border-loss-DEFAULT/40 bg-loss-DEFAULT/10 text-loss-DEFAULT';
  return (
    <span className={cn('text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded font-semibold', cls)}>
      {active ? 'activo' : 'desactivado'}
    </span>
  );
}

function InvitationStatusBadge({ status }: { status: string }): ReactElement {
  const cls =
    status === 'pending'
      ? 'border-brand-500/40 bg-brand-500/10 text-brand-500'
      : status === 'accepted'
        ? 'border-win-DEFAULT/40 bg-win-DEFAULT/10 text-win-DEFAULT'
        : 'border-surface-border bg-surface-panel text-ink-muted line-through';
  return (
    <span className={cn('text-[10px] uppercase tracking-wider border px-2 py-0.5 rounded font-semibold', cls)}>
      {status}
    </span>
  );
}

function SectionSkeleton({ label }: { label: string }): ReactElement {
  return (
    <div className="rounded-xl border border-surface-border bg-surface-elevated p-10 text-center text-ink-subtle text-sm">
      {label}
    </div>
  );
}

function SectionEmpty({ msg }: { msg: string }): ReactElement {
  return (
    <div className="rounded-xl border border-dashed border-surface-border bg-surface-elevated/50 p-10 text-center text-ink-muted text-sm">
      {msg}
    </div>
  );
}

function SectionError({ msg }: { msg: string }): ReactElement {
  return (
    <div className="rounded-xl border border-loss-DEFAULT/30 bg-loss-DEFAULT/10 p-6 text-loss-DEFAULT text-sm">
      Error: {msg}
    </div>
  );
}
